import RetailerDropShippingInfoDTO from "@/dto/order/RetailerDropShippingInfoDTO";
import LogisticsCompany from "@/enum/LogisticsCompany";
import TaskStatusEnum from "@/enum/TaskStatus";
import { get, post } from "@/services/http/platformServer";

export interface CommitGiftItemDto {
    dropShippingGiftSkuId: number;
    dropShippingGiftProductId: number;
    quantity: number;
}

export interface CommitOrderItemDto {
    customizationInfo: string;
    dropShippingSkuId: number;
    picture: string;
    quantity: number;
    skuProperties: string;
    title: string;
    originalId: string;
}

/**提交订单参数 */
export interface RetailerOrderPushParams {
    buyerRemark?: string;
    giftItems?: CommitGiftItemDto[];
    logisticsCompany?: LogisticsCompany;
    orderItems?: CommitOrderItemDto[];
    /**代理商的订单id */
    retailerOrderId: number;
    /**代理商的shop id */
    retailerShopId: number;
    /**此字段意思为node系统对应main系统的companyId */
    supplierShopId: number;
}

/**审核结果 */
interface ApprovalResult {
    errorMsg: string;
    orderId: number;
    successful: boolean;
}

/**
 * 供货商调用审核订单
 */
export function approvalOrders(shopId: number, supplierShopId: number, orderIds: number[]) {
    return post<ApprovalResult[]>(shopId, "/drop_shipping_order/approval", {
        orderIds,
        supplierShopId,
    });
}

/**代理商发起支付请求 */
export function payOrder(shopId: number, retailerOrderId: number) {
    return post(shopId, "/drop_shipping_order/pay", {
        retailerShopId: shopId,
        retailerOrderId,
    });
}

interface BatchPayOrderData {
    retailerOrderId: number;

    retailerShopId: number;
}

export interface BatchPaidResult {
    errorMsg: string;

    retailerOrderId: number;

    retailerShopId: number;

    successful: boolean;
}

export interface OrderPayProcess {
    results: BatchPaidResult[];

    errorMsg: string;

    status: "COMPLETED" | "ERROR" | "RUNNING";
}

/**
 * 代理商批量发起支付请求
 */
export function batchPayOrder(shopId: number, data: BatchPayOrderData[]) {
    return post<OrderPayProcess>(shopId, "/drop_shipping_order/batch_pay", data);
}

/**
 * 获取批量支付请求进度
 */
export function getBatchPayOrderProcess(shopId: number) {
    return get<OrderPayProcess>(shopId, "/drop_shipping_order/batch_pay_process");
}

export interface PendingOrderAmount {
    amount: number;

    supplierShopId: number;
}

/**
 * 获取代理商代销未支付订单的总金额
 */
export function getSupplierPendingPaymentTotalAmount(
    shopId: number,
    supplierShopIds: number[],
): Promise<PendingOrderAmount[]> {
    return post(shopId, "/drop_shipping_order/pending_payment/total_amount", {
        retailerShopId: shopId,
        supplierShopIds,
    });
}

export interface PushOrderParams {
    retailerOrders: RetailerOrderPushParams[];
    retailerShopId: number;
    supplierShopId: number;
}

export interface PushOrderResult {
    errorMsg?: string;
    retailerOrderId: number;
    retailerShopId: number;
    successful: boolean;
    supplierShopId: number;
    retailerDropShippingInfo?: RetailerDropShippingInfoDTO;
}

export interface BatchOrderAsyncPushProcess {
    errorMsg?: string;

    results: PushOrderResult[];

    status: TaskStatusEnum;

    taskId: string;
}

/**
 * 代理商批量提交分销订单(异步)
 */
export function asyncDropShippingOrderBatchPush(
    shopId: number,
    params: PushOrderParams,
): Promise<BatchOrderAsyncPushProcess> {
    return post(shopId, "/drop_shipping_order/async_batch_push", params);
}

export function asyncDropShippingOrderBatchPushProcess(
    shopId: number,
    taskId: string,
): Promise<BatchOrderAsyncPushProcess> {
    return get(shopId, "/drop_shipping_order/async_batch_order_push_process", { taskId });
}

/**
 * 供货商取消订单
 */
export function cancelOrdersBySupplier(params: { supplierOrderIds: number[]; supplierShopId: number }) {
    const shopId = params.supplierShopId;

    return post(shopId, "/drop_shipping_order/supplier_order/cancel", params);
}

interface SyncCustomizationInfoToSupplierParams {
    retailerOrderId: number;
    retailerShopId: number;
}

export function syncCustomizationInfoToSupplier(params: SyncCustomizationInfoToSupplierParams) {
    return post(params.retailerShopId, "/drop_shipping_order/sync_customization_info_to_supplier", params);
}

interface BatchUpdateDropShippingRemarksParams {
    supplierShopId: number;
    supplierOrderIds: number[];
    remarks: string;
    appended: boolean;
}

export interface UpdateDropShippingRemarksResult {
    /**
     * 更新后的代发信息
     */
    dropShippingRemarks: string;
    /**
     * 错误信息，更新失败时有值
     */
    error?: string;
    /**
     * 供货商订单ID
     */
    supplierOrderId: number;
    /**
     * 供货商店铺ID
     */
    supplierShopId: number;
}

export function batchUpdateDropShippingRemark(params: BatchUpdateDropShippingRemarksParams) {
    return post<UpdateDropShippingRemarksResult[]>(
        params.supplierShopId,
        "/drop_shipping_order/batch_update_drop_shipping_remarks",
        params,
    );
}
